/**
 * Componente para manejar el callback de autenticación de Azure AD
 * Procesa el código de autorización devuelto por Azure AD
 */

import React, { useEffect, useState, useRef } from 'react';
import styled from 'styled-components';
import { extractAuthCode, extractError, cleanUrl } from '../../utils/azureAuth';
import { useAuth } from '../../contexts/AuthContext';
import apiConfig from '../../config/api.config';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  padding: 2rem;
`;

const Card = styled.div`
  background: white;
  border-radius: ${({ theme }) => theme.borderRadius.xl};
  padding: 3rem;
  box-shadow: ${({ theme }) => theme.shadows.xl};
  text-align: center;
  max-width: 400px;
  width: 100%;
`;

const Title = styled.h1`
  color: ${({ theme }) => theme.colors.textPrimary};
  margin-bottom: 0.5rem;
  font-size: 2rem;
  font-weight: 600;
`;

const Message = styled.p`
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-bottom: 1rem;
  font-size: 1.1rem;
  line-height: 1.5;
`;

const ErrorBox = styled.div`
  color: ${({ theme }) => theme.colors.error};
  background: ${({ theme }) => theme.colors.error}10;
  padding: 1rem;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  margin-bottom: 1rem;
  border: 1px solid ${({ theme }) => theme.colors.error}30;
`;

const Spinner = styled.div`
  border: 4px solid #f3f3f3;
  border-top: 4px solid #10b981;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  animation: spin 1s linear infinite;
  margin: 0 auto 1rem;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const Link = styled.a`
  color: #10b981;
  text-decoration: none;
  font-weight: bold;

  &:hover {
    text-decoration: underline;
  }
`;

const AzureCallback: React.FC = () => {
  const [status, setStatus] = useState<'processing' | 'success' | 'error'>('processing');
  const [error, setError] = useState<string | null>(null);
  const hasProcessed = useRef(false);
  const { loginWithAzure } = useAuth();

  useEffect(() => {
    // Prevenir ejecuciones múltiples (React StrictMode en desarrollo)
    if (hasProcessed.current) {
      console.log('=== Callback ya procesado, ignorando ejecución duplicada ===');
      return;
    }

    const processCallback = async () => {
      try {
        console.log('=== AzureCallback iniciado ===');
        console.log('URL actual:', window.location.href);
        console.log('URL params:', window.location.search);

        // Marcar como procesado ANTES de hacer la petición
        hasProcessed.current = true;

        // Verificar si hay errores
        const authError = extractError();
        console.log('Auth error:', authError);
        if (authError.error) {
          setError(`Error de autenticación: ${authError.error_description || authError.error}`);
          setStatus('error');
          return;
        }

        // Extraer código de autorización
        const authCode = extractAuthCode();
        console.log('Auth code:', authCode);
        if (!authCode) {
          // Si no hay código, redirigir a la página principal
          console.log('No hay código de autorización, redirigiendo...');
          window.location.href = '/';
          return;
        }

        // Enviar código al backend para intercambiarlo por token
        console.log('Enviando código al backend...');
        const response = await fetch(apiConfig.endpoints.azureCallback, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ code: authCode })
        });

        console.log('Response status:', response.status);

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          console.log('Error response:', errorData);
          const detail = errorData.azure_error || errorData.details || errorData.error;
          throw new Error(detail || `Error: ${response.status}`);
        }

        const result = await response.json();
        console.log('Backend result:', result);
        
        if (result.success) {
          // Usar la función de login de Azure del contexto
          await loginWithAzure(result.access_token, result.proveedor);
          
          setStatus('success');
          // Limpiar la URL de parámetros
          cleanUrl();
          
          // Redirigir a la aplicación principal
          setTimeout(() => {
            window.location.href = '/';
          }, 1500);
        } else {
          setError(result.error || 'Error desconocido');
          setStatus('error');
        }

      } catch (err: any) {
        console.error('Error en callback:', err);
        setError(err.message || 'Error procesando la autenticación');
        setStatus('error');
      }
    };

    processCallback();
  }, [loginWithAzure]);

  if (status === 'processing') {
    return (
      <Container>
        <Card>
          <Spinner />
          <Title>Procesando autenticación...</Title>
          <Message>
            Por favor espera mientras completamos tu inicio de sesión con Microsoft Azure.
          </Message>
        </Card>
      </Container>
    );
  }

  if (status === 'error') {
    return (
      <Container>
        <Card>
          <Title>❌ Error de autenticación</Title>
          <ErrorBox>
            {error}
          </ErrorBox>
          <Message>
            <Link href="/">
              Volver al inicio
            </Link>
          </Message>
        </Card>
      </Container>
    );
  }

  if (status === 'success') {
    return (
      <Container>
        <Card>
          <Title>✅ ¡Autenticación exitosa!</Title>
          <Message>
            Te estamos redirigiendo a la aplicación...
          </Message>
        </Card>
      </Container>
    );
  }

  return null;
};

export default AzureCallback;

